/**
 * THE RENTALS SIDE: TODAY AT THE YARD, AND THE BOARD.
 *
 * Anthony, 2026-09-28, for WeRV: "rv rental on one side of their crm, rv
 * repair on the other, in one dash with a button to swap between the
 * two", and then "have a list of customers coming in and when they are
 * coming and for what on the dash".
 *
 * ─── WHY THE HOME IS A LIST AND NOT THE BOARD ─────────────────────────
 *
 * The board answers "where is every trip", which is a planning question
 * asked a few times a day. The question at the gate at eight in the
 * morning is "who is turning up, when, and is their RV ready". So the
 * Rentals home (#/ on this side, "Today" on the bar) is that list, grouped
 * Today, Tomorrow and the next seven days, with overdue returns above it
 * in red, and a strip of lane counts that opens the board. The repair
 * side's own bookings are on it too, labelled Repair, because a yard that
 * fixes RVs has one gate.
 *
 * ─── THE BOARD ─────────────────────────────────────────────────────────
 *
 * Nine lanes as columns, scrolling sideways on a desk, and stacked one
 * above the next on a phone, where a sideways scroller of nine columns is
 * a strip nobody can read. A card is the renter, the RV, the dates and the
 * money, with flags for what needs doing: delivered, pets, a balance due,
 * a deposit on hold, overdue, and "should be in Prep" when the calendar
 * says it has moved on and nobody has pressed the button yet. The board
 * suggests and a person moves it (src/domain/rental.ts, laneDue).
 *
 * Everything reads through /api/rentals (server/routes/rentals.ts).
 */

import { esc, money, mount } from "../ui/dom.ts";
import { rentalsApi } from "../data/rentals.ts";
import {
  LANES, STAGE_LABEL, backLine, balanceUrgent, isDemoTrip, isOverdue, laneDue, shortDay, unitName, type Trip,
} from "../domain/rental.ts";
import { ARRIVAL_LABEL, type Arrival, type ArrivalGroups } from "../domain/rentalday.ts";

/** A sentence for a failure, or for the rentals side not being switched on yet. */
export function rentalsDown(error: string, missing?: boolean): string {
  return missing
    ? `<div class="empty">The rentals side is not switched on for this shop yet. It needs one database change,
         and then this screen fills in by itself.</div>`
    : `<div class="empty bad">${esc(error)}</div>`;
}

/** "(951) 555-0100" from ten digits, or the digits as they came. */
export const dialable = (p: string | null | undefined): string => {
  const d = String(p ?? "").replace(/\D/g, "").slice(-10);
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : String(p ?? "");
};

/** Call and text, for a row or a trip. Nothing when there is no number. */
export function reachLinks(phone: string | null | undefined): string {
  const d = String(phone ?? "").replace(/\D/g, "").slice(-10);
  if (d.length !== 10) return "";
  return `<a class="btn sm" href="tel:+1${d}">Call</a><a class="btn sm" href="sms:+1${d}">Text</a>`;
}

/* ── demo trips ──────────────────────────────────────────────────────── */

/**
 * DEMO TRIPS ARE SHOWING. Anthony, 2026-09-28: "Put one demo lead in every
 * stage with an option to delete them or archive them easily on the
 * rental side when we're done with the demo." The trips are written by
 * tools/shops/rental-demo.mjs and marked in their notes (isDemoTrip in
 * src/domain/rental.ts). While any exist, the board and Today carry this
 * one line and a button, and the button asks once, in the page, before it
 * removes them. Nothing when there are none, so a yard that never had a
 * demo never sees it.
 */
export function demoNotice(count: number): string {
  if (!count) return "";
  return `<div class="rdemo" id="rdemo"><span><span class="pill rdemo-pill">Demo</span>
      ${count} demo trip${count === 1 ? " is" : "s are"} showing.</span>
    <button type="button" class="btn sm" data-demo-clear>Remove demo trips</button>
    <div class="rconfirm bad" id="rdemo-confirm" hidden>
      <p>Delete the ${count} demo trip${count === 1 ? "" : "s"} and their walk rounds? Trips anybody typed in are not touched.</p>
      <div class="racts"><button type="button" class="btn danger solid sm" data-demo-go>Delete the demo trips</button>
        <button type="button" class="btn ghost sm" data-demo-keep>Keep them</button></div>
      <p class="msg bad" id="rdemo-msg" hidden></p></div></div>`;
}

/** The notice's buttons. `again` redraws the screen it sits on once they are gone. */
export function wireDemoNotice(again: () => void): void {
  const box = document.getElementById("rdemo-confirm");
  document.querySelector<HTMLButtonElement>("[data-demo-clear]")?.addEventListener("click", () => { if (box) box.hidden = false; });
  document.querySelector<HTMLButtonElement>("[data-demo-keep]")?.addEventListener("click", () => { if (box) box.hidden = true; });
  document.querySelector<HTMLButtonElement>("[data-demo-go]")?.addEventListener("click", async (e) => {
    const b = e.currentTarget as HTMLButtonElement;
    b.disabled = true;
    const r = await rentalsApi<{ removed: number }>("/demo/clear", "POST", {});
    b.disabled = false;
    if (!r.ok) {
      const m = document.getElementById("rdemo-msg");
      if (m) { m.textContent = r.error; m.hidden = false; }
      return;
    }
    again();
  });
}

const demoPill = `<span class="pill rdemo-pill">Demo</span>`;

/* ── today at the yard ───────────────────────────────────────────────── */

type TodayAnswer = { today: string; groups: ArrivalGroups; lanes: Record<string, number>; demo_ids?: string[] };

export async function rentalsHome(): Promise<void> {
  mount(`<div class="page">
    <header class="page-head"><h1>Today at the Yard</h1>
      <p>Who is coming in, when, and what for. Pickups, returns, deliveries and repair bookings.</p></header>
    <div class="rhead-acts"><a class="btn primary" href="#/newtrip">New trip</a>
      <a class="btn" href="#/rentals">The board</a></div>
    <div id="rent-demo"></div>
    <div id="rent-lanes"></div>
    <div id="rent-today"><div class="card"><div class="loading">Reading&hellip;</div></div></div>
  </div>`);
  const res = await rentalsApi<TodayAnswer>("/today");
  const host = document.getElementById("rent-today");
  if (!host) return;
  if (!res.ok) { host.innerHTML = `<div class="card">${rentalsDown(res.error, res.missing)}</div>`; return; }
  const { groups, lanes, today } = res.data;
  const demo = new Set(res.data.demo_ids ?? []);
  const demoHost = document.getElementById("rent-demo");
  if (demoHost) { demoHost.innerHTML = demoNotice(demo.size); wireDemoNotice(() => void rentalsHome()); }

  const strip = document.getElementById("rent-lanes");
  if (strip) {
    strip.innerHTML = `<nav class="rlanestrip" aria-label="Trips in each lane">${LANES.filter((l) => l.key !== "closed").map((l) =>
      `<a href="#/rentals" class="rlanechip"><b>${lanes[l.key] ?? 0}</b><span>${esc(l.label)}</span></a>`).join("")}</nav>`;
  }

  const when = (d: string): string => new Date(`${d}T12:00:00Z`)
    .toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" });
  const row = (a: Arrival, showDay: boolean): string => `
    <div class="row${a.overdue ? " rrow-late" : ""}">
      <div class="row-main">
        <div class="row-title"><a class="num" href="${esc(a.href)}">${esc(a.name)}</a>
          <span class="pill rkind rk-${esc(a.kind)}">${esc(a.overdue ? `${ARRIVAL_LABEL[a.kind]}, overdue` : ARRIVAL_LABEL[a.kind])}</span>${
            demo.has(a.href.replace(/^#\/trip\//, "")) ? demoPill : ""}</div>
        <div class="row-sub">${esc(a.kind === "repair" ? a.what : a.unit)}${a.kind === "repair" && a.unit ? ` &middot; ${esc(a.unit)}` : ""}</div>
        ${a.address ? `<div class="row-note">We drive to: ${esc(a.address)}</div>` : ""}
        <div class="hq-actions">${reachLinks(a.phone)}<a class="btn ghost sm" href="${esc(a.href)}">Open</a></div>
      </div>
      <div class="row-side"><div class="when">${esc(showDay || a.overdue ? when(a.day) : "")}</div>
        <div class="amount">${esc(a.time)}</div></div>
    </div>`;
  const group = (title: string, list: Arrival[], showDay: boolean, bad = false) => list.length ? `
    <div class="card"><div class="card-head${bad ? " bad" : ""}">${esc(title)} &middot; ${list.length}</div>
      ${list.map((a) => row(a, showDay)).join("")}</div>` : "";
  const any = groups.overdue.length + groups.today.length + groups.tomorrow.length + groups.week.length;
  host.innerHTML = `
    ${group("Overdue returns", groups.overdue, true, true)}
    ${group(`Today, ${when(today)}`, groups.today, false)}
    ${group("Tomorrow", groups.tomorrow, false)}
    ${group("Next 7 days", groups.week, true)}
    ${any ? "" : `<div class="card"><div class="empty">Nobody is due in the next week. New bookings and
      repair appointments show here as they are made.</div></div>`}`;
}

/* ── the board ───────────────────────────────────────────────────────── */

type TripsAnswer = { today: string; may_write: boolean; demo_count?: number; trips: Trip[] };

function card(t: Trip, today: string): string {
  const flags: string[] = [];
  if (isDemoTrip(t)) flags.push(demoPill);
  if (isOverdue(t.stage, t.end_date, today)) flags.push(`<span class="pill rflag-bad">Overdue</span>`);
  if (balanceUrgent(t, today)) flags.push(`<span class="pill rflag-bad">Balance due</span>`);
  if (t.delivery) flags.push(`<span class="pill">Delivery</span>`);
  if (t.pets > 0) flags.push(`<span class="pill">Pets ${t.pets}</span>`);
  if (t.deposit_status === "held") flags.push(`<span class="pill">Deposit held</span>`);
  const due = laneDue(t.stage, t.start_date, t.end_date, today);
  if (due) flags.push(`<span class="pill rflag-go">Should be in ${esc(STAGE_LABEL[due])}</span>`);
  const dates = t.start_date && t.end_date
    ? `${shortDay(t.start_date)} to ${shortDay(t.end_date)} &middot; ${t.nights ?? 0} night${t.nights === 1 ? "" : "s"}`
    : "No dates yet";
  return `<a class="rcard" href="#/trip/${esc(t.id)}">
      <span class="rcard-who">${esc(t.renter_name || "No name yet")}</span>
      <span class="rcard-unit">${esc(unitName(t.unit) || "No RV picked")}</span>
      <span class="rcard-dates">${dates}</span>
      ${t.stage === "out" || t.stage === "returning" ? `<span class="rcard-dates">${esc(backLine(t.stage, t.end_date, today))}</span>` : ""}
      <span class="rcard-foot"><b>${t.quoted_total !== null ? money(t.quoted_total) : ""}</b>${flags.join("")}</span>
    </a>`;
}

export async function rentalsBoard(): Promise<void> {
  mount(`<div class="page page-wide">
    <header class="page-head"><h1>The Board</h1>
      <p>Every trip, in its lane. Tap one to move it on, file a walk round or settle the deposit.</p></header>
    <div class="rhead-acts"><a class="btn primary" href="#/newtrip">New trip</a>
      <a class="btn" href="#/rentcal">Calendar</a><a class="btn ghost" href="#/workflow">How a rental runs</a></div>
    <div id="rent-demo"></div>
    <div id="rent-board"><div class="card"><div class="loading">Reading&hellip;</div></div></div>
  </div>`);
  const [open, closed] = await Promise.all([
    rentalsApi<TripsAnswer>("/trips?stage=inquiry,quoted,booked,prep,out,returning,inspection,settled"),
    rentalsApi<TripsAnswer>("/trips?stage=closed"),
  ]);
  const host = document.getElementById("rent-board");
  if (!host) return;
  if (!open.ok) { host.innerHTML = `<div class="card">${rentalsDown(open.error, open.missing)}</div>`; return; }
  const today = open.data.today;
  const demoHost = document.getElementById("rent-demo");
  if (demoHost) { demoHost.innerHTML = demoNotice(open.data.demo_count ?? 0); wireDemoNotice(() => void rentalsBoard()); }
  /* Closed trips are history. The last two weeks of them, so a trip closed
     this morning is still where somebody looks for it. */
  const recent = closed.ok ? closed.data.trips.filter((t) => (t.end_date ?? t.updated_at.slice(0, 10)) >= addBack(today, 14)) : [];
  const all = [...open.data.trips, ...recent];
  host.innerHTML = `<div class="rboard">${LANES.map((l) => {
    const list = all.filter((t) => t.stage === l.key);
    return `<section class="rlane" aria-label="${esc(l.label)}">
      <div class="rlane-h"><span>${esc(l.label)}</span><i>${list.length}</i></div>
      ${list.map((t) => card(t, today)).join("") || `<div class="rlane-empty">None</div>`}
    </section>`;
  }).join("")}</div>`;
}

const addBack = (d: string, n: number): string => new Date(Date.parse(`${d}T12:00:00Z`) - n * 86_400_000).toISOString().slice(0, 10);
